<template>
  <div class="flex flex-col gap-2">
    <div v-if="mode == 'add' || mode == 'edit'" class="pb-8 border-b-4">
      <FormValidator ref="form">
        <Field symbol="body" class="pt-4">
          <ZLabel for="body">
            {{ $t('contracts.label.message_body') }}
          </ZLabel>
          <textarea
            id="body"
            v-model="contractMessage.body"
            rows="4"
            class="flex w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </Field>
      </FormValidator>
      <div class="flex gap-2 pt-4">
        <ZButton :disabled="false" variant="secondary" @click="viewMessage()">{{ $t('common.cancel') }} </ZButton>
        <Action :disabled="false" @proceed="storeDraftMessage">{{ $t('contracts.command.create_message') }} </Action>
      </div>
    </div>

    <div v-if="mode == 'view'" class="pb-8 border-b-4">
      <div class="flex gap-2 items-center">
        <ZLabel>{{ $t('contracts.label.message_body') }}</ZLabel> <ZButton :disabled="false" variant="outline" @click="editMessage()"><PencilIcon class="icon sm" /> ... </ZButton>
      </div>
      <div class="whitespace-pre-line border p-4 rounded my-2">
        {{ contractMessage.body }}
        <div class="flex gap-2 mt-2 pt-2 border-t items-center">
          <span><b><PaperClipIcon class="icon sm"/></b></span>
          <span v-for="att in contractMessage!.attachments" class="flex gap-4">
            <span class="flex">[<a href="#" @click="detachFile(att.id)"><TrashIcon class="icon sm text-red-600"/></a> <a :href="att.url" class="text-amber-900 text-decoration-line" target="_blank">{{att.name + '.' + att.ext}}</a>]</span>
          </span>
          <ZButton variant="outline" @click="attachFile">{{ $t('contracts.command.message_attach_file') }}</ZButton>
        </div>
      </div>
      <IconAlert type="warning" v-if="contractMessage.state == 'draft'">{{ $t('contracts.message.message_in_draft') }}</IconAlert>
      <div class="flex gap-2 pt-4 w-full justify-between">
        <div class="flex gap-2">
          <ZButton :disabled="false" variant="secondary" @click="listMessages()">{{ $t('common.cancel') }} </ZButton>
          <ZButton :disabled="false" variant="destructive" @click="deleteMessage()" v-if="contractMessage.state == 'draft'"><TrashIcon class="sm icon"/> {{ $t('contracts.command.delete_message') }} </ZButton>
        </div>
        <div v-if="contractMessage.state == 'draft'">
          <ZButton :disabled="false" @click="sendMessage()">{{ $t('contracts.command.send_message') }} </ZButton>
        </div>
      </div>

      <FileAttach
        ref="attachFileDialog"
        :on-update="addFileAsAttachment"
        type="contractMessage"
        :id="contractMessage.id!"
        :organization-context="organizationId"
      />
    </div>

    <div>
      <div class="border rounded-lg">
        <ZTable class="w-full">
          <ZTableHeader>
            <ZTableRow>
              <ZTableHead>{{ $t('contracts.label.timestamp') }}</ZTableHead>
              <ZTableHead>{{ $t('contracts.label.author') }}</ZTableHead>
              <ZTableHead>{{ $t('contracts.label.comment') }}</ZTableHead>
            </ZTableRow>
          </ZTableHeader>
          <ZTableBody>
            <template v-for="(item, index) in messagesDataSet.data" :key="index">
              <ZTableRow>
                <ZTableCell class="flex flex-col min-w-[160px] align-top">
                  <span class="text-xs text-gray-400">{{ formatDate(item!.created_at!) }}</span>
                  <div>
                    <pretty-status :status="item!.state" translate="contract.messageStatus."></pretty-status>
                  </div>
                </ZTableCell>
                <ZTableCell class="min-w-[160px] align-top">
                  <div class="flex flex-col align-top">
                    <b>{{ item!.user_name }}</b>
                    {{ item!.party }}
                  </div>
                </ZTableCell>
                <ZTableCell>
                  <div class="whitespace-pre-line py-2">
                    {{ item!.body }}
                  </div>
                  <div class="flex gap-2 border-t mt-2 mb-1" v-if="item.attachments.length > 0">
                    <a :href="att.url" target="_blank" v-for="att in item!.attachments" class="flex gap-1 px-2 py-2">
                      <PaperClipIcon class="icon sm"/>
                      {{att.name + '.' + att.ext}}
                    </a>
                  </div>
                  <div class="flex justify-between">
                    <div>
                      <span
                        class="rounded-[6px] text-[10px] text-gray-800 py-1 px-2 uppercase font-bold text-center bg-orange-300"
                        v-if="item.needs_answer && item.answers == 0 && item.state == 'sent'">
                        {{ $t('contract.messageStatus.awaiting_response') }}
                      </span>
                      <div class="flex items-center gap-2" v-if="item.needs_answer && item.answers > 0">
                        <span
                          class="text-[10px] text-gray-800 py-1 uppercase font-bold text-center"
                        >{{ $t('contracts.label.answers') }} : </span>
                        <span
                          class="rounded-[6px] text-[10px] text-gray-800 py-1 px-2 uppercase font-bold text-center bg-yellow-400"
                        >{{ item.answers }}</span>
                      </div>
                      <div v-if="item.answer_to">
                        <span
                          class="rounded-[6px] text-[10px] text-gray-800 py-1 px-2 uppercase font-bold text-center bg-yellow-400"
                        >{{ $t('contracts.label.answer') }}</span>
                      </div>
                    </div>
                    <ZButton class="h-7" @click="addMessage(item.id)" v-if="item.needs_answer && item.state == 'sent'">
                        <span class="ml-2">
                          {{ $t('contracts.command.answer') }}
                        </span>
                    </ZButton>
                  </div>
                </ZTableCell>
                <ZTableCell>
                  <ZButton variant="ghost" @click="viewMessage(item)" v-if="access.currentUser()?.id == item.user_id">
                    <EllipsisHorizontalIcon class="icon sm" />
                  </ZButton>
                </ZTableCell>
              </ZTableRow>
            </template>
          </ZTableBody>
        </ZTable>
        <div v-if="messagesDataSet.data?.length == 0" class="p-8 text-center flex flex-col gap-2 items-center">
          <ExclamationTriangleIcon class="icon lg" />
          {{ $t('contracts.message.changes_not_found') }}
        </div>
      </div>
      <div class="flex items-center">
        <Paginator :data="messagesDataSet"></Paginator>
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue';
import { Dialogs } from '@/types/Dialogs';
import Navigation from '@/types/Navigation';
import IconAlert from '@ui/IconAlert.vue';
import { ComponentLoadState } from '@/types/ComponentLoadState';
import {
  createContractMessage,
  deleteContractMessage,
  getContractMessages,
  sendContractMessage,
  updateContractMessage,
  viewContractMessage,
} from '@/api/contract';
import { ZTable, ZTableBody, ZTableCell, ZTableHead, ZTableHeader, ZTableRow } from '@shadcn/components/ui/table';
import { DataSet } from '@/utils/DataSet';
import Paginator from '@ui/Paginator.vue';
import PrettyStatus from '@ui/PrettyStatus.vue';
import {ChevronDownIcon, ExclamationTriangleIcon, EllipsisHorizontalIcon, PencilIcon, PaperClipIcon, TrashIcon} from '@modules/@heroicons/vue/24/outline';
import ContractMessageData = App.Data.ContractMessageData;
import {ZButton} from "@shadcn/components/ui/button";
import {ZDialogFooter} from "@shadcn/components/ui/dialog";
import {ZLabel} from "@shadcn/components/ui/label";
import FormValidator from "@ui/FormValidator.vue";
import Field from "@ui/FormField.vue";
import Action from "@ui/Action.vue";
import {Checkbox} from "@shadcn/components/ui/checkbox";
import ContractMessageDataStoreRequest = App.Data.ContractMessageDataStoreRequest;
import FileAttach from "@ui/File/FileAttach.vue";
import {detachFile, http} from "@/api";
import useAccessStore from "@/stores/access";

export default defineComponent({
  components: {
    EllipsisHorizontalIcon,
    PencilIcon,
    PaperClipIcon,
    TrashIcon,
    FileAttach,
    Checkbox, Action, Field, FormValidator, ZLabel, ZDialogFooter,
    ChevronDownIcon, ZButton,
    ExclamationTriangleIcon,
    PrettyStatus,
    Paginator,
    ZTableCell,
    ZTableHeader,
    ZTableRow,
    ZTable,
    ZTableHead,
    ZTableBody,
    IconAlert,
  },

  props: {
    contractId: {
      type: String,
      required: true,
    },
    organizationId: {
      type: String,
      required: true,
    },
  },

  computed: {
    access: () => useAccessStore(),
  },

  setup() {
    return {
      dialogs: inject('dialogs') as Dialogs,
      toast: inject('toast') as (props: any) => void,
      navigation: inject('navigation') as Navigation,
    };
  },

  data() {
    return {
      state: 'loading' as ComponentLoadState,
      messagesDataSet: {} as DataSet<ContractMessageData, null>,
      mode: 'list' as string,
      contractMessage: {
        body: '',
        needs_answer: true
      } as ContractMessageDataStoreRequest,
    };
  },

  async mounted() {
    try {
      await this.getMessages();
      this.state = 'loaded';
    } catch (error: any) {
      this.state = 'error';
      throw error;
    }
  },

  methods: {
    async getMessages() {
      this.messagesDataSet = new DataSet<ContractMessageData, null>((query: string) =>
        getContractMessages(this.contractId).execute(query),
      );
      await this.messagesDataSet.load();
    },

    listMessages() {
      this.contractMessage = {
        body: '',
        needs_answer: true
      };

      this.mode = 'list';
      this.getMessages();
    },

    async viewMessage(message:ContractMessageData|null) {
      if (message != null) this.contractMessage = message;
      this.mode = 'view';
      const response = await viewContractMessage(this.contractId, this.contractMessage.id).execute();
      this.contractMessage = (response.data as ContractMessageData);

      this.getMessages();
    },

    editMessage(message:ContractMessageData|null) {
      if (message != null) this.contractMessage = message;
      this.mode = 'edit';
    },

    addMessage(to: string) {
      this.contractMessage.answer_to = to;
      this.mode = 'add';
    },

    attachFile() {
      (this.$refs['attachFileDialog'] as typeof FileAttach).show();
    },

    addFileAsAttachment(file: any) {
      this.viewMessage();
    },

    async detachFile(id) {
      try {
        await this.dialogs.confirm(this.$t('contracts.label.dialog_detach_attachment'), this.$t('contracts.message.dialog_detach_attachment'));
        await detachFile({
          fileId: id,
          destinationId: this.contractMessage.id,
          destinationType: 'contractMessage'
        }).execute();

        const messageResponse = await viewContractMessage(this.contractId, this.contractMessage.id).execute();
        this.contractMessage = messageResponse.data;
      } catch(e: any) {
      }
    },

    storeDraftMessage(action: any) {
      if (this.mode == 'add') {
        createContractMessage(this.contractId, this.contractMessage)
          .withForm(this.$refs.form as typeof FormValidator)
          .withLoader(action.loader)
          .execute()
          .then(response => {
            this.viewMessage(response.data as ContractMessageData);

            this.toast({
              title: this.$t('toasts.type.success'),
              description: this.$t('contracts.message.message_created'),
            });
          });
      } else {
        updateContractMessage(this.contractId, this.contractMessage.id, this.contractMessage)
          .withForm(this.$refs.form as typeof FormValidator)
          .withLoader(action.loader)
          .execute()
          .then(response => {
            this.viewMessage(response.data as ContractMessageData);

            this.toast({
              title: this.$t('toasts.type.success'),
              description: this.$t('contracts.message.message_updated'),
            });
          });
      }
    },

    sendMessage() {
      this.dialogs.confirm(this.$t('contracts.label.dialog_send_message'), this.$t('contracts.message.dialog_send_message')).then(
        () => {
          sendContractMessage(this.contractId, this.contractMessage.id).execute().then(response => {
            this.listMessages();

            this.toast({
              title: this.$t('toasts.type.success'),
              description: this.$t('contracts.message.message_sent'),
            });
          });
        });
    },

    deleteMessage() {
      this.dialogs.confirm(this.$t('contracts.label.dialog_delete_message'), this.$t('contracts.message.dialog_delete_message')).then(
        () => {
          deleteContractMessage(this.contractId, this.contractMessage.id).execute();
          this.listMessages();

          this.toast({
            title: this.$t('toasts.type.success'),
            description: this.$t('contracts.message.message_deleted'),
          });
        });
    }
  },
});
</script>
